import React, { useEffect, useRef } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArticleJsonLd } from 'next-seo'

import * as S from './styles'
import { usePosts } from './usePosts'
import { BlogCard, BlogPagination, Text } from '@/components'
import {
  FilterContainer,
  FilterContainerSelected,
  FilterContainerOnClickTagParam,
  FeaturedArticleContainer,
} from '@/containers'
import { GetPostsResponse } from '@/service/api/blog'
import { toStructuredData } from '@/service/api/blog/blogMapper'

export interface PostsContainerProps {
  posts: GetPostsResponse
  categories: string[]
}

export const PostsContainer: React.FC<PostsContainerProps> = ({
  posts,
  categories,
}) => {
  const { articleHighlight, text } = posts
  const router = useRouter()
  const locale = useRef(router.locale)

  const {
    filterBy,
    setFilterBy,
    categoriesData,
    setCategoriesData,
    setFilterByCategory,
    filterByCategory,
    currentPage,
    setCurrentPage,
    numberPages,
    postsToRender,
    onChange,
    search,
  } = usePosts({ posts, categories })

  useEffect(() => {
    // force rerender when changing locale
    if (locale.current !== router.locale) {
      locale.current = router.locale
      setCategoriesData(categories)
      setFilterByCategory('')
      setCurrentPage(1)
    }
  }, [
    router.locale,
    categories,
    setCategoriesData,
    setFilterByCategory,
    filterByCategory,
    setCurrentPage,
  ])

  const notRepeatedCategoriesData = Array.from(new Set(categoriesData))

  const handlePage = (page: number) => {
    if (currentPage > numberPages) return null

    window.scrollTo(0, 0)
    return setCurrentPage(page)
  }

  const postsLength = postsToRender.length > 0
  return (
    <S.Container>
      <S.Title size="h2" color="darkgray">
        {text.title}
      </S.Title>
      <FilterContainer
        onClick={(value: FilterContainerSelected) => {
          setFilterBy(value)
        }}
        onClickTag={(tag: FilterContainerOnClickTagParam) => {
          if (tag === filterByCategory) {
            return setFilterByCategory('')
          }
          return setFilterByCategory(tag)
        }}
        title={filterByCategory || text.tagsTitle}
        text={text.filter}
        options={notRepeatedCategoriesData}
        selected={filterBy}
        selectedByCategory={filterByCategory}
        onChange={onChange}
        search={search}
      />
      <S.Main>
        <Link href={`/blog/${articleHighlight.slug}`} passHref>
          <ArticleJsonLd {...toStructuredData(articleHighlight)} />
          <FeaturedArticleContainer data={articleHighlight} />
        </Link>
        {postsLength ? (
          <S.List>
            {postsToRender.map((post) => (
              <Link key={post.slug} href={`/blog/${post.slug}`} passHref>
                <ArticleJsonLd {...toStructuredData(post)} />
                <BlogCard {...post} author={post.author} />
              </Link>
            ))}
          </S.List>
        ) : (
          <div className="not-results">
            <Text size="h5" color="darkgray">
              {text.notFoundMessage}
            </Text>
          </div>
        )}
      </S.Main>
      {postsLength && (
        <BlogPagination
          currentPage={currentPage}
          pageCount={numberPages}
          setCurrentPage={handlePage}
        />
      )}
    </S.Container>
  )
}
